import { Node } from 'prosemirror-model'
import {
  Command,
  EditorState,
  TextSelection,
  Transaction,
} from 'prosemirror-state'

import { schema } from '@showrunner/prose-schemas'

import { findVisibleSelection } from '#choo-app/lib/editor/plugins/titlePageView/helpers'
import { getPageLayout } from '#util/PageLayout'

import { getConfigData } from '../configData'

import { titlePageViewPluginKey } from './titlePageViewPlugin'

/**
 * The meat of the title page, centered above the footer
 */
const content = [
  schema.text('SCRIPT TITLE', [schema.marks.underline.create()]),
  schema.text('Written by'),
  schema.text('Name of First Writer'),
  schema.text('Based on, If Any'),
]

/**
 * The footer content, takes up the bottom few lines of the page
 */
const footer = schema.text('Contact Information')

/**
 * Build a title page that fits the current document's dimensions and spacing.
 * Does a bunch of fussy stuff to make it look exactly like Final Draft if we
 * can support it (when the document's general text format has no top margin)
 */
export function initialTitlePage(state: EditorState) {
  const configData = getConfigData(state)
  const layout = getPageLayout(configData.currentPageConfig)
  const { lineHeight, blockTopMargin } = configData.currentBlockFormats.general

  const blockHeight = lineHeight + blockTopMargin
  const availableContentHeight = Math.floor(layout.maxLinesPerPage - 4)

  let effectiveBlockHeight = blockHeight
  let contentHeight = effectiveBlockHeight * content.length + 9

  if (blockTopMargin !== 0) {
    effectiveBlockHeight = blockHeight * 2
    contentHeight = effectiveBlockHeight * content.length
  }

  const remainingHeight = availableContentHeight - contentHeight
  const spacingHeight = Math.max(Math.floor(remainingHeight / 2), 0)

  const children = []
  const spacer = schema.nodes.general.create({ alignment: 'center' })

  for (let i = 0; i < spacingHeight; i += blockHeight) children.push(spacer)
  for (const line of content) {
    children.push(schema.nodes.general.create({ alignment: 'center' }, line))
    if (blockTopMargin === 0) {
      if (line.text?.includes('TITLE')) children.push(spacer, spacer, spacer)
      if (line.text?.includes('Written by')) children.push(spacer, spacer)
      if (line.text?.includes('Name of')) {
        children.push(spacer, spacer, spacer, spacer)
      }
    }
  }
  for (let i = 0; i < spacingHeight; i += blockHeight) children.push(spacer)
  children.push(schema.nodes.general.create(null, footer))

  return schema.nodes.titlePage.create(null, children)
}

/**
 * Update the title page's visiblity for this transaction
 */
export function setTitlePageVisibility(tr: Transaction, visible: boolean) {
  return tr.setMeta(titlePageViewPluginKey, { showTitlePageView: visible })
}

/**
 * Command to set the title page view mode
 */
export function setTitlePageView(showTitlePageView: boolean): Command {
  return (state, dispatch, view) => {
    if (dispatch) {
      const tr = setTitlePageVisibility(state.tr, showTitlePageView)
      const selection = findVisibleSelection(state, showTitlePageView)
      if (selection) tr.setSelection(selection)
      dispatch(tr)
      if (view) view.focus()
    }
    return true
  }
}

const insertTitlePageIntoDocument = (tr: Transaction, page: Node) => {
  let cursorPos = 0
  tr.insert(cursorPos, page)

  page.descendants((node, pos) => {
    if (cursorPos > 0) return false
    if (node.text) cursorPos = pos + node.nodeSize + 1
  })

  return cursorPos
}

const insertTitlePageBreak = (tr: Transaction, node: Node) => {
  const pageBreakNode = schema.nodes.general.create({
    alignment: 'center',
    pageBreak: 'manual',
  })
  const emptyBlock = schema.nodes.general.create({ alignment: 'center' })
  const insertPos = node.nodeSize - 1
  tr.insert(insertPos, pageBreakNode)
  tr.insert(insertPos + pageBreakNode.nodeSize, emptyBlock)
  return insertPos + pageBreakNode.nodeSize + 1
}

/**
 * Command to create or add to the title page
 * If no title page exists, creates one with initial content
 * If a title page exists, adds a page break to it
 */
export const createTitlePage: Command = (state, dispatch, view) => {
  const { schema } = state
  const { firstChild } = state.doc

  if (!firstChild) return false

  if (dispatch) {
    const tr = state.tr

    const insertPos =
      firstChild.type !== schema.nodes.titlePage
        ? insertTitlePageIntoDocument(tr, initialTitlePage(state))
        : insertTitlePageBreak(tr, firstChild)

    const newSelection = TextSelection.near(tr.doc.resolve(insertPos))
    tr.setSelection(newSelection)

    setTitlePageVisibility(tr, true)

    dispatch(tr.scrollIntoView())

    if (view) view.focus()
  }

  return true
}

/**
 * Command to delete the title page from the beginning of the document
 * Returns true if the title page was deleted, false if no title page exists
 */
export const deleteTitlePage: Command = (state, dispatch) => {
  const { firstChild } = state.doc
  if (!firstChild) return false
  if (firstChild.type !== schema.nodes.titlePage) return false

  if (dispatch) {
    const tr = state.tr.delete(0, firstChild.nodeSize)
    setTitlePageVisibility(tr, false)
    dispatch(tr)
  }

  return true
}
